import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ShoppingBag, 
  Truck, 
  Users, 
  Leaf, 
  Clock, 
  CreditCard,
  MapPin,
  ChevronRight
} from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection, AnimatedHeading } from "@/components/ui/AnimatedSection";
import { FeatureCard } from "@/components/ui/FeatureCard";
import { TestimonialCard } from "@/components/ui/TestimonialCard";
import logo from "@/assets/logo.webp";
import gemueseregalImg from "@/assets/gemueseregal.webp";
import heroImg from "@/assets/rewe-aussenaufnahme.webp";
import partnerPaulsBauernhof from "@/assets/partner-pauls-bauernhof.webp";
import partnerSchreibers from "@/assets/partner-schreibers.webp";
import partnerObsthofScherer from "@/assets/partner-obsthof-scherer.webp";
import partnerFlickWeine from "@/assets/partner-flick-weine.webp";
import { SEO } from "@/components/SEO";

const features = [
  {
    icon: <ShoppingBag className="w-7 h-7" />,
    title: "Vollsortiment",
    description: "Alles für deinen täglichen Bedarf unter einem Dach – von frischen Lebensmitteln bis zu Haushaltswaren.",
  },
  {
    icon: <Truck className="w-7 h-7" />,
    title: "Abholservice",
    description: "Bequem online bestellen und fertig gepackt abholen. Spart Zeit und ist stressfrei!",
  },
  {
    icon: <Leaf className="w-7 h-7" />,
    title: "Regionale Produkte",
    description: "Wir arbeiten eng mit lokalen Partnern zusammen, um dir frische, regionale Qualität anzubieten.",
  },
  {
    icon: <Users className="w-7 h-7" />,
    title: "Persönlicher Service",
    description: "Unser freundliches Team steht dir mit Rat und Tat zur Seite – bei uns bist du kein Kunde, sondern Gast.",
  },
];

const testimonials = [
  {
    name: "Julian M.",
    text: "Super REWE! Hat gefühlt alles! Auch viele ausgefallene Sachen kann man hier kaufen. Das Personal ist super freundlich und die Preise sind auch super! Sehr zufrieden!",
  },
  {
    name: "Nicole K.",
    text: "Gut sortierter, kleiner REWE mit sehr freundlichem Personal!",
  },
  {
    name: "Louis S.",
    text: "Man kennt das Personal und das Personal kennt einen auch. Es ist immer sauber, die Regale sind gefüllt, alles wunderbar.",
  },
  {
    name: "Jonas G.",
    text: "Sehr freundliches Personal, gut sortierter Markt und mein Lieblings-Supermarkt im Umkreis.",
  },
];

const partners = [
  { name: "Paul's Bauernhof", url: "https://www.pauls-bauernhof.de/", logo: partnerPaulsBauernhof, width: 366, height: 144 },
  { name: "Weingut Schreiber", url: "https://weingut-schreiber.de/", logo: partnerSchreibers, width: 600, height: 98 },
  { name: "Flick Weine", url: "https://shop.flick-wein.de/", logo: partnerFlickWeine, width: 430, height: 600 },
  { name: "Obsthof Martin Scherer", url: "https://hessische-direktvermarkter.de/betriebe/obsthof-martin-scherer/", logo: partnerObsthofScherer, width: 600, height: 395 },
];

export default function Home() {
  return (
    <Layout>
      <SEO title="REWE V. Gahndi | Supermarkt in Flörsheim-Weilbach" description="Dein REWE Markt in Flörsheim-Weilbach – Vollsortiment, Abholservice, regionale Produkte. Mo–Sa 7–22 Uhr." path="/" />
      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/75" />

        <div className="relative container-custom text-center py-12 md:py-10 lg:py-20 flex flex-col min-h-[100svh] md:min-h-[70vh] lg:min-h-[80vh]">
          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <img
                src={logo}
                alt="REWE V. Gahndi"
                className="h-20 md:h-28 lg:h-32 w-auto mx-auto"
              />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-primary uppercase tracking-[0.3em] text-sm md:text-base mb-4"
            >
              Herzlich Willkommen
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-semibold mb-6"
            >
              REWE Gahndi in{" "}
              <span className="text-gold-gradient">Flörsheim-Weilbach</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10"
            >
              Qualität, Frische und regionale Produkte von lokalen Partnern. 
              Bei uns findest du alles für deinen täglichen Bedarf – mit persönlichem Service 
              und einem Lächeln.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/produkte-service" className="btn-gold-filled">
                Entdecke unser Sortiment
              </Link>
              <a
                href="https://goo.gl/maps/VJQynYQHgqsTnpz99"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center gap-2"
              >
                <MapPin className="w-4 h-4" />
                Anfahrt planen
              </a>
            </motion.div>
          </div>

          {/* Quick Info - positioned at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-auto pt-8 flex flex-wrap justify-center gap-8 text-sm text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              <span>Mo. – Sa.: 7:00 – 22:00 Uhr</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              <span>Alle gängigen Zahlungsmethoden</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              <span>115 kostenlose Parkplätze</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Unser Angebot">
            Was uns <span className="text-gold-gradient">auszeichnet</span>
          </AnimatedHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={index * 0.1}
              />
            ))}
          </div>

          {/* Abholservice CTA */}
          <AnimatedSection className="mt-16" delay={0.2}>
            <div className="card-premium border-gold-glow text-center p-8 md:p-12">
              <Truck className="w-12 h-12 text-primary mx-auto mb-6" />
              <h3 className="text-2xl md:text-3xl font-heading font-semibold mb-4">
                Nutze unseren <span className="text-gold-gradient">Abholservice</span>
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                Bestelle bequem online und hole deine Einkäufe fertig gepackt ab. 
                Spare Zeit und genieße dennoch die volle Produktvielfalt unseres Marktes.
              </p>
              <Link to="/produkte-service" className="btn-gold-filled">
                Mehr erfahren
                <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-square"
              >
                <img 
                  src={gemueseregalImg} 
                  alt="Frische Gemüseauswahl im REWE Markt" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <p className="text-primary uppercase tracking-widest text-sm font-medium mb-3 text-center lg:text-left">
                Über uns
              </p>
              <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6 text-center lg:text-left">
                Mehr als nur ein <span className="text-gold-gradient">Supermarkt</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6 text-center lg:text-left">
                Als Vollsortimenter versorgen wir unsere Kunden mit köstlichen Frischeprodukten, 
                einer heißen Theke, reichhaltigen Molkereiwaren, einer üppigen Auswahl an Getränken 
                sowie einer Vielzahl an weiteren Highlights. Ganz besonders sticht unsere eigene 
                Fleischerei heraus, in der viele hochwertige Produkte in eigener Herstellung 
                produziert werden.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8 text-center lg:text-left">
                Bei uns findest du außerdem die Bäckerei Schäfer direkt am Markt. Wir legen 
                großen Wert auf regionale Waren und arbeiten mit vielen ortsansässigen Bauern 
                zusammen. Dadurch ist unser Produktangebot immer saisonal geprägt.
              </p>
              <div className="text-center lg:text-left">
                <Link to="/ueber-uns" className="btn-gold">
                  Mehr über uns
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Regional Partners */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Unsere Partner">
            Regionale <span className="text-gold-gradient">Qualität</span>
          </AnimatedHeading>

          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
              Bei REWE Gahndi arbeiten wir eng mit regionalen Partnern zusammen und verkaufen 
              deren hochwertige Produkte. So unterstützen wir lokale Erzeuger und bieten dir 
              frische Qualität aus der Region.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {partners.map((partner, index) => (
              <AnimatedSection key={partner.name} delay={index * 0.1}>
                <a
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center h-32 p-4 border border-border rounded-2xl transition-all duration-300 hover:border-primary/50"
                  style={{ background: 'white' }}
                  aria-label={`Partner: ${partner.name} (öffnet in neuem Tab)`}
                >
                  <img 
                    src={partner.logo} 
                    alt={partner.name}
                    width={partner.width}
                    height={partner.height}
                    loading="lazy"
                    className="max-h-20 max-w-full w-auto object-contain"
                  />
                </a>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection className="text-center mt-12" delay={0.4}>
            <Link to="/regionales" className="btn-gold">
              Alle Partner entdecken
              <ChevronRight className="w-4 h-4 ml-2" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Kundenstimmen">
            Was unsere Kunden <span className="text-gold-gradient">sagen</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.name}
                name={testimonial.name}
                text={testimonial.text}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-b from-secondary to-background">
        <div className="container-custom">
          <AnimatedSection>
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold mb-6">
                Wir freuen uns auf <span className="text-gold-gradient">deinen Besuch</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-10">
                Überzeuge dich selbst von unserem Angebot, der Qualität unserer Produkte 
                und dem freundlichen Service. Wir sind montags bis samstags von 7:00 bis 22:00 Uhr 
                für dich da.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://goo.gl/maps/VJQynYQHgqsTnpz99"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold-filled inline-flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  Route planen
                </a>
                <Link to="/kontakt" className="btn-gold">
                  Kontakt aufnehmen
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
